/**
 * Commissions Module — Financials → Commissions
 * 
 * Per-rep earnings, per-deal breakdown, bonus logic, payout totals.
 * Data sourced exclusively from validated Appointment Results.
 */
import { useState, useMemo } from "react";
import { useAppData } from "@/data/AppDataContext";
import { useFinancialPermissions } from "@/hooks/useFinancialPermissions";
import { useCurrentRep } from "@/hooks/useCurrentRep";
import {
  computeRepScorecard, TIER_CONFIG, BASE_COMMISSION_RATE, CLOSE_RATE_BONUSES,
  SAME_DAY_CLOSE_BONUS, FINANCING_BONUS_FLAT, RECOVERY_BONUS_FLAT,
  type RepScorecard, type DealCommission,
} from "@/data/commissionEngine";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { format, parseISO } from "date-fns";
import {
  DollarSign, Banknote, Award, TrendingUp, CheckCircle,
  ShieldCheck, Users, Receipt, Percent,
} from "lucide-react";

function currency(v: number): string { return `$${Math.round(v).toLocaleString()}`; }
function pct(v: number): string { return `${Math.round(v * 100)}%`; }

export default function Commissions() {
  const { appointmentResults, followUpTasks, jobs } = useAppData();
  const fin$ = useFinancialPermissions();
  const { repName: CURRENT_REP } = useCurrentRep();
  // Sales reps see only their own scorecard
  const scopedResults = fin$.scopedToOwn
    ? appointmentResults.filter((r) => r.assignedRep === CURRENT_REP)
    : appointmentResults;
  const [selectedRep, setSelectedRep] = useState<string | null>(fin$.scopedToOwn ? CURRENT_REP : null);

  const reps = useMemo((): string[] =>
    [...new Set(scopedResults.map(r => r.assignedRep))].sort(),
    [scopedResults]
  );

  const scorecards = useMemo((): RepScorecard[] => {
    return reps.map(rep => {
      const repTasks = followUpTasks.filter(t => t.assignedTo === rep);
      const completedTasks = repTasks.filter(t => t.status === "completed");
      const followUpRate = repTasks.length > 0 ? completedTasks.length / repTasks.length : 1.0;
      return computeRepScorecard(rep, appointmentResults, followUpRate, jobs);
    });
  }, [reps, appointmentResults, followUpTasks, jobs]);

  const totals = useMemo(() => ({
    totalCommission: scorecards.reduce((s, sc) => s + sc.totalEarned, 0),
    totalRevenue: scorecards.reduce((s, sc) => s + sc.totalRevenueSold, 0),
    totalDeals: scorecards.reduce((s, sc) => s + sc.salesClosed, 0),
    totalBase: scorecards.reduce((s, sc) => s + sc.totalBaseCommission, 0),
    totalSameDay: scorecards.reduce((s, sc) => s + sc.totalSameDayBonus, 0),
    totalFinancing: scorecards.reduce((s, sc) => s + sc.totalFinancingBonus, 0),
    totalRecovery: scorecards.reduce((s, sc) => s + sc.totalRecoveryBonus, 0),
  }), [scorecards]);

  const activeScorecard = selectedRep ? scorecards.find(s => s.repName === selectedRep) : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
            <Receipt className="h-5 w-5 text-primary" />
            Commissions
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Compensation calculated from validated Appointment Results only
          </p>
        </div>
        <Badge variant="outline" className="text-[10px] gap-1">
          <ShieldCheck className="h-3 w-3" /> Immutable Data Source
        </Badge>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {([
          { label: "Total Payout", value: currency(totals.totalCommission), icon: Banknote, color: "text-success" },
          { label: "Revenue Basis", value: currency(totals.totalRevenue), icon: DollarSign, color: "text-primary" },
          { label: "Deals Paid", value: totals.totalDeals, icon: CheckCircle, color: "text-success" },
          { label: "Effective Rate", value: totals.totalRevenue > 0 ? pct(totals.totalCommission / totals.totalRevenue) : "0%", icon: Percent, color: "text-warning" },
        ] as const).map(kpi => (
          <Card key={kpi.label} className="border-border/50">
            <CardContent className="p-3 flex items-center gap-3">
              <kpi.icon className={cn("h-5 w-5", kpi.color)} />
              <div>
                <p className="text-lg font-bold">{kpi.value}</p>
                <p className="text-[10px] text-muted-foreground">{kpi.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Bonus Breakdown */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-border/50"><CardContent className="p-3 text-center">
          <p className="text-[10px] text-muted-foreground">Base Commission</p>
          <p className="text-sm font-bold">{currency(totals.totalBase)}</p>
          <p className="text-[9px] text-muted-foreground">{pct(BASE_COMMISSION_RATE)} rate</p>
        </CardContent></Card>
        <Card className="border-border/50"><CardContent className="p-3 text-center">
          <p className="text-[10px] text-muted-foreground">Same-Day Bonuses</p>
          <p className="text-sm font-bold text-success">{currency(totals.totalSameDay)}</p>
          <p className="text-[9px] text-muted-foreground">+{pct(SAME_DAY_CLOSE_BONUS)} per deal</p>
        </CardContent></Card>
        <Card className="border-border/50"><CardContent className="p-3 text-center">
          <p className="text-[10px] text-muted-foreground">Financing Bonuses</p>
          <p className="text-sm font-bold text-primary">{currency(totals.totalFinancing)}</p>
          <p className="text-[9px] text-muted-foreground">{currency(FINANCING_BONUS_FLAT)} per deal</p>
        </CardContent></Card>
        <Card className="border-border/50"><CardContent className="p-3 text-center">
          <p className="text-[10px] text-muted-foreground">Recovery Bonuses</p>
          <p className="text-sm font-bold text-warning">{currency(totals.totalRecovery)}</p>
          <p className="text-[9px] text-muted-foreground">{currency(RECOVERY_BONUS_FLAT)} per deal</p>
        </CardContent></Card>
      </div>

      {/* Commission Structure Reference */}
      <Card className="border-border/50">
        <CardContent className="p-4">
          <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Commission Structure</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
            <div>
              <p className="font-semibold">Base Rate</p>
              <p className="text-muted-foreground">{pct(BASE_COMMISSION_RATE)} of revenue</p>
            </div>
            {CLOSE_RATE_BONUSES.map(b => (
              <div key={b.label}>
                <p className="font-semibold">{b.label}</p>
                <p className="text-muted-foreground">+{pct(b.bonus)} bonus</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="by-rep" className="space-y-4">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="by-rep" className="text-xs gap-1.5">
            <Users className="h-3.5 w-3.5" /> By Rep
          </TabsTrigger>
          <TabsTrigger value="all-deals" className="text-xs gap-1.5">
            <Receipt className="h-3.5 w-3.5" /> All Deals
          </TabsTrigger>
        </TabsList>

        <TabsContent value="by-rep">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Rep List */}
            <div className="space-y-2">
              <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">Reps ({scorecards.length})</p>
              {scorecards.length === 0 ? (
                <Card className="border-border/50">
                  <CardContent className="p-6 text-center text-sm text-muted-foreground">
                    No commission data yet. Results will appear after reps submit appointment outcomes marked as Sold.
                  </CardContent>
                </Card>
              ) : (
                scorecards.map(sc => {
                  const tier = TIER_CONFIG[sc.performanceTier];
                  return (
                    <button
                      key={sc.repName}
                      onClick={() => setSelectedRep(sc.repName)}
                      className={cn(
                        "w-full text-left p-3 rounded-lg border transition-all",
                        selectedRep === sc.repName
                          ? "border-primary/40 bg-primary/5 ring-1 ring-primary/20"
                          : "border-border/50 bg-card hover:border-border"
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-[10px] font-bold text-primary">{sc.repName.split(" ").map(n => n[0]).join("")}</span>
                          </div>
                          <div>
                            <p className="text-xs font-semibold">{sc.repName}</p>
                            <Badge variant="outline" className={cn("text-[9px] h-4 mt-0.5", tier.color, tier.bg)}>{tier.label}</Badge>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs font-bold text-success">{currency(sc.totalEarned)}</p>
                          <p className="text-[9px] text-muted-foreground">{sc.salesClosed} deals</p>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>

            {/* Detail View */}
            <div className="lg:col-span-2">
              {activeScorecard ? (
                <Card className="border-border/50">
                  <CardContent className="p-5 space-y-4">
                    {/* Header */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-sm font-bold text-primary">{activeScorecard.repName.split(" ").map(n => n[0]).join("")}</span>
                        </div>
                        <div>
                          <h3 className="text-base font-bold">{activeScorecard.repName}</h3>
                          <p className="text-[10px] text-muted-foreground">
                            {pct(activeScorecard.effectiveRate)} effective rate ({pct(BASE_COMMISSION_RATE)} base + {pct(activeScorecard.closeRateBonusRate)} close rate bonus)
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-success">{currency(activeScorecard.totalEarned)}</p>
                        <p className="text-[10px] text-muted-foreground">Total Earned</p>
                      </div>
                    </div>

                    {/* Bonus Summary */}
                    <div className="grid grid-cols-4 gap-2">
                      <div className="p-2 rounded-md bg-muted/50 text-center">
                        <p className="text-[9px] text-muted-foreground">Base</p>
                        <p className="text-xs font-bold">{currency(activeScorecard.totalBaseCommission)}</p>
                      </div>
                      <div className="p-2 rounded-md bg-success/5 text-center">
                        <p className="text-[9px] text-muted-foreground">Same-Day</p>
                        <p className="text-xs font-bold text-success">{currency(activeScorecard.totalSameDayBonus)}</p>
                      </div>
                      <div className="p-2 rounded-md bg-primary/5 text-center">
                        <p className="text-[9px] text-muted-foreground">Financing</p>
                        <p className="text-xs font-bold text-primary">{currency(activeScorecard.totalFinancingBonus)}</p>
                      </div>
                      <div className="p-2 rounded-md bg-warning/5 text-center">
                        <p className="text-[9px] text-muted-foreground">Recovery</p>
                        <p className="text-xs font-bold text-warning">{currency(activeScorecard.totalRecoveryBonus)}</p>
                      </div>
                    </div>

                    <Separator />

                    {/* Deal-level breakdown */}
                    {activeScorecard.deals.length > 0 ? (
                      <div className="overflow-x-auto">
                        <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Deal Breakdown</p>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="text-[10px]">Customer</TableHead>
                              <TableHead className="text-[10px]">Date</TableHead>
                              <TableHead className="text-[10px] text-right">Revenue</TableHead>
                              <TableHead className="text-[10px] text-right">Base</TableHead>
                              <TableHead className="text-[10px] text-right">Same-Day</TableHead>
                              <TableHead className="text-[10px] text-right">Financing</TableHead>
                              <TableHead className="text-[10px] text-right">Recovery</TableHead>
                              <TableHead className="text-[10px] text-right font-semibold">Total</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {activeScorecard.deals.map(d => (
                              <TableRow key={d.resultId}>
                                <TableCell className="text-xs font-medium">{d.customerName}</TableCell>
                                <TableCell className="text-[10px] text-muted-foreground">{format(parseISO(d.appointmentDate), "MMM d")}</TableCell>
                                <TableCell className="text-xs text-right">{currency(d.revenue)}</TableCell>
                                <TableCell className="text-xs text-right">{currency(d.baseAmount)}</TableCell>
                                <TableCell className="text-xs text-right">{d.sameDayBonus > 0 ? <span className="text-success">{currency(d.sameDayBonus)}</span> : "—"}</TableCell>
                                <TableCell className="text-xs text-right">{d.financingBonus > 0 ? <span className="text-primary">{currency(d.financingBonus)}</span> : "—"}</TableCell>
                                <TableCell className="text-xs text-right">{d.recoveryBonus > 0 ? <span className="text-warning">{currency(d.recoveryBonus)}</span> : "—"}</TableCell>
                                <TableCell className="text-xs text-right font-bold text-success">{currency(d.totalDeal)}</TableCell>
                              </TableRow>
                            ))}
                            <TableRow className="border-t-2">
                              <TableCell colSpan={7} className="text-xs font-bold text-right">Total Earned</TableCell>
                              <TableCell className="text-sm font-bold text-right text-success">{currency(activeScorecard.totalEarned)}</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground text-center py-4">No commission deals recorded yet.</p>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-border/50">
                  <CardContent className="p-12 text-center text-muted-foreground">
                    <Banknote className="h-8 w-8 mx-auto mb-3 opacity-50" />
                    <p className="text-sm font-medium">Select a rep to view their commission details</p>
                    <p className="text-xs mt-1">Per-deal breakdown with bonus attribution</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="all-deals">
          {(() => {
            const allDeals = scorecards.flatMap(sc =>
              sc.deals.map(d => ({ ...d, repName: sc.repName }))
            ).sort((a, b) => new Date(b.appointmentDate).getTime() - new Date(a.appointmentDate).getTime());

            return allDeals.length > 0 ? (
              <Card className="border-border/50">
                <CardContent className="p-4 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-[10px]">Rep</TableHead>
                        <TableHead className="text-[10px]">Customer</TableHead>
                        <TableHead className="text-[10px]">Date</TableHead>
                        <TableHead className="text-[10px] text-right">Revenue</TableHead>
                        <TableHead className="text-[10px] text-right">Base</TableHead>
                        <TableHead className="text-[10px] text-right">Bonuses</TableHead>
                        <TableHead className="text-[10px] text-right font-semibold">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {allDeals.map(d => (
                        <TableRow key={d.resultId}>
                          <TableCell className="text-xs font-medium">{d.repName}</TableCell>
                          <TableCell className="text-xs">{d.customerName}</TableCell>
                          <TableCell className="text-[10px] text-muted-foreground">{format(parseISO(d.appointmentDate), "MMM d, yyyy")}</TableCell>
                          <TableCell className="text-xs text-right">{currency(d.revenue)}</TableCell>
                          <TableCell className="text-xs text-right">{currency(d.baseAmount)}</TableCell>
                          <TableCell className="text-xs text-right">{currency(d.sameDayBonus + d.financingBonus + d.recoveryBonus)}</TableCell>
                          <TableCell className="text-xs text-right font-bold text-success">{currency(d.totalDeal)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-border/50">
                <CardContent className="p-12 text-center text-muted-foreground">
                  <p className="text-sm">No commission deals recorded yet.</p>
                </CardContent>
              </Card>
            );
          })()}
        </TabsContent>
      </Tabs>
    </div>
  );
}
